import { useEECEvents } from 'analytics/hooks/useEECEvents/useEECEvents'
import type { ShoppingCartAddItemInput } from 'codegen/src/gateway/types'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { DEFAULT_LOOK_ID } from 'product/constants/constants'
import { DEFAULT_SELLER } from '../../constants/constants'
import {
	AddItemDocument,
	type AddItemMutation,
	type AddItemMutationVariables,
} from '../../services/mutations/add-item.generated'
import type { Item } from '../../types/ControllerType'
import { checkItemStock } from '../../utils/checkItemStock'
import { isTokenError } from '../../utils/exceptions'
import { getMngItems } from '../../utils/getMngItems'
import { useCartFetcher } from '../useCartFetcher'
import { useNotifications } from '../useNotifications'
import { useUpdateBag } from './useUpdateBag'

export interface ProductType {
	collectionId?: string
	colorId: string
	customColor?: string
	customPosition?: string
	customSize?: string
	customType?: string
	look: string
	productId: string
	needleWork?: string
	sizeId: string
	seller: string
	lastUnits: boolean
	layoutView?: string
}

export type AddItemVariable = {
	item: ShoppingCartAddItemInput
	canApplyPromoCode: boolean
}

const _MAX_RETRIES = 3

export const useAddToCart = (isCartPage: boolean) => {
	const {
		country: { countryISO },
	} = useMasterData()
	const { fetcher } = useCartFetcher<
		AddItemMutation,
		AddItemMutationVariables
	>()
	const { addItemNotificationError } = useNotifications()
	const { updateBag } = useUpdateBag()
	const { sendAddToCart } = useEECEvents()

	const getLookId = (lookId: string) => lookId?.toString() || DEFAULT_LOOK_ID

	const prepareItem = (product: ProductType) => {
		const {
			collectionId,
			colorId,
			customColor,
			customPosition,
			customSize,
			customType,
			look,
			productId,
			needleWork,
			sizeId,
			seller = DEFAULT_SELLER,
		} = product
		const customized = {
			collectionId: collectionId?.toString(),
			customColor,
			customPosition,
			customSize,
			customType,
			needleWork,
		}

		const item: Item = {
			colorId,
			productId,
			seller,
			sizeId,
			look: getLookId(look),
		}

		if (
			customized &&
			Object.values(customized).some((attribute) => !!attribute)
		) {
			item.customized = customized
		}
		return item
	}

	const addItem = async (
		product: ProductType,
		location: string,
		productListId?: string,
		productListName?: string
	) => {
		const item = prepareItem(product)

		for (let attempts = 0; attempts < _MAX_RETRIES; attempts++) {
			try {
				// biome-ignore lint/performance/noAwaitInLoops: sequential retries are required to recover from transient token errors
				await checkItemStock(item, countryISO)

				const response = await fetcher([
					AddItemDocument,
					{ item, canApplyPromoCode: isCartPage ?? false },
				])

				await updateBag(response.addItem)

				await sendAddToCart({
					item: {
						...item,
						lastUnits: product.lastUnits,
						quantity: 1,
						index: 1,
					},
					mngItems: getMngItems(
						response.addItem?.items?.filter((itm) => itm !== null)
					),
					listId: productListId,
					listName: productListName,
					location,
				})

				return response
			} catch (error) {
				const isToken = isTokenError(error)

				if (!isToken) {
					addItemNotificationError()
					console.error(error)
					return null
				}

				console.warn(
					`[useAddToCart] Token error, retrying... attempt ${attempts + 1}`
				)
			}
		}

		console.error('[useAddToCart] Max retries reached')
		addItemNotificationError()
		return null
	}

	return { addItem }
}
